/**
 * ManualTracksList — Lista editable y agrupada de canciones-rol del DJ.
 *
 * Iter101 (feb-2026): edit inline + drag&drop.
 * Iter101.1 (feb-2026): agrupado por flujo real del DJ:
 *   🔵 Warmup                       — invitados llegando, sin novios todavía
 *   🟢 Opener + Bachata early       — arranque tras la entrada de novios
 *   🔥 Peak + Imprescindible        — repartidas por el set
 *   🔴 Cierre                       — closer simbólico
 *
 * Comportamiento drag&drop:
 *   - Dentro del MISMO grupo  → reordena prioridad (POST /reorder con los IDs
 *     del grupo en su nuevo orden).
 *   - Entre GRUPOS distintos  → PATCH role al rol por defecto del grupo destino +
 *     refresh local. El rol específico (ej. bachata_early dentro de "Opener+Bachata
 *     early") se sigue ajustando con el dropdown inline de cada track.
 *
 * Endpoints usados:
 *   GET    /api/dj-assistant/manual-tracks         (ordenados por priority asc)
 *   PATCH  /api/dj-assistant/manual-tracks/:id     (edit inline)
 *   POST   /api/dj-assistant/manual-tracks/reorder ({ids: [...]} nueva prioridad)
 *   DELETE /api/dj-assistant/manual-tracks/:id
 */
import { useState, useRef, useMemo } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ROLE_OPTIONS = [
  { value: 'opener', emoji: '🟢', label: 'Opener' },
  { value: 'warmup', emoji: '🔵', label: 'Warmup' },
  { value: 'bachata_early', emoji: '💃', label: 'Bachata early' },
  { value: 'peak', emoji: '🔥', label: 'Peak' },
  { value: 'imprescindible', emoji: '⭐', label: 'Imprescindible' },
  { value: 'closer', emoji: '🔴', label: 'Closer' },
];
const ROLE_EMOJIS = ROLE_OPTIONS.reduce((m, r) => { m[r.value] = r.emoji; return m; }, {});

// Iter101.1 — Grupos visuales según workflow del DJ.
const GROUPS = [
  { id: 'warmup',   label: 'Warmup',                emoji: '🔵', roles: ['warmup'],              defaultRole: 'warmup' },
  { id: 'opener',   label: 'Opener + Bachata early',emoji: '🟢', roles: ['opener', 'bachata_early'], defaultRole: 'opener' },
  { id: 'peak',     label: 'Peak + Imprescindible', emoji: '🔥', roles: ['peak', 'imprescindible'],  defaultRole: 'peak' },
  { id: 'closer',   label: 'Cierre',                emoji: '🔴', roles: ['closer'],              defaultRole: 'closer' },
];

const roleToGroupId = (role) => {
  for (const g of GROUPS) if (g.roles.includes(role)) return g.id;
  return 'opener'; // fallback razonable
};

const SortableTrack = ({ track, api, token, onUpdate, onDelete, indexInGroup }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });
  const [editingField, setEditingField] = useState(null);
  const [savingField, setSavingField] = useState(false);
  const titleRef = useRef(null);
  const artistRef = useRef(null);
  const hintsRef = useRef(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const patchField = async (patch) => {
    setSavingField(true);
    try {
      const res = await axios.patch(
        `${api}/dj-assistant/manual-tracks/${track.id}`,
        patch,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) onUpdate(res.data.track);
    } catch (e) {
      toast.error(`Error guardando: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavingField(false);
      setEditingField(null);
    }
  };

  const handleTitleBlur = () => {
    const v = titleRef.current?.value?.trim();
    if (v && v !== track.title) patchField({ title: v }); else setEditingField(null);
  };
  const handleArtistBlur = () => {
    const v = (artistRef.current?.value ?? '').trim();
    if (v !== (track.artist || '')) patchField({ artist: v }); else setEditingField(null);
  };
  const handleHintsBlur = () => {
    const raw = (hintsRef.current?.value ?? '').trim();
    const newHints = raw ? raw.split(',').map(h => h.trim()).filter(Boolean) : [];
    const oldHints = (track.event_type_hints || []).join(',');
    if (newHints.join(',') !== oldHints) patchField({ event_type_hints: newHints });
    else setEditingField(null);
  };
  const handleRoleChange = (newRole) => {
    if (newRole !== track.role) patchField({ role: newRole });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 bg-slate-800/60 rounded px-2 py-1.5 text-xs hover:bg-slate-800/90 transition-colors"
      data-testid={`dj-assistant-manual-track-${track.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-slate-500 hover:text-purple-300 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
        title="Arrastra para reordenar o cambiar de grupo"
        data-testid={`dj-assistant-manual-track-drag-${track.id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-[10px] text-slate-500 w-5 text-right tabular-nums flex-shrink-0">#{indexInGroup + 1}</span>
      <span className="text-base flex-shrink-0">{ROLE_EMOJIS[track.role] || '🎵'}</span>

      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-2">
          {editingField === 'title' ? (
            <input
              ref={titleRef}
              defaultValue={track.title || ''}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditingField(null); }}
              autoFocus
              className="flex-1 min-w-0 bg-slate-950 text-white border border-purple-500/40 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
              data-testid={`dj-assistant-manual-track-title-input-${track.id}`}
            />
          ) : (
            <button
              onClick={() => setEditingField('title')}
              className="flex-1 min-w-0 text-white truncate text-left hover:text-purple-300"
              title="Clic para editar título"
              data-testid={`dj-assistant-manual-track-title-${track.id}`}
            >
              {track.title || <span className="italic text-slate-500">(sin título)</span>}
            </button>
          )}
          <select
            value={track.role || 'opener'}
            onChange={(e) => handleRoleChange(e.target.value)}
            disabled={savingField}
            className="flex-shrink-0 bg-slate-900 border border-slate-700 rounded text-[10px] px-1 py-0.5 text-purple-300 hover:border-purple-500 focus:outline-none disabled:opacity-50"
            data-testid={`dj-assistant-manual-track-role-${track.id}`}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.emoji} {r.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 text-slate-400">
          {editingField === 'artist' ? (
            <input
              ref={artistRef}
              defaultValue={track.artist || ''}
              onBlur={handleArtistBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditingField(null); }}
              autoFocus
              placeholder="Artista"
              className="flex-1 min-w-0 bg-slate-950 text-slate-200 border border-purple-500/40 rounded px-1 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
              data-testid={`dj-assistant-manual-track-artist-input-${track.id}`}
            />
          ) : (
            <button
              onClick={() => setEditingField('artist')}
              className="truncate text-left hover:text-purple-300 max-w-[40%]"
              title="Clic para editar artista"
              data-testid={`dj-assistant-manual-track-artist-${track.id}`}
            >
              {track.artist || <span className="italic text-slate-500">(sin artista)</span>}
            </button>
          )}
          <span className="text-slate-600">·</span>
          {editingField === 'hints' ? (
            <input
              ref={hintsRef}
              defaultValue={(track.event_type_hints || []).join(',')}
              onBlur={handleHintsBlur}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditingField(null); }}
              autoFocus
              placeholder="boda,fiesta,empresa..."
              className="flex-1 min-w-0 bg-slate-950 text-slate-300 border border-purple-500/40 rounded px-1 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-purple-500"
              data-testid={`dj-assistant-manual-track-hints-input-${track.id}`}
            />
          ) : (
            <button
              onClick={() => setEditingField('hints')}
              className="truncate text-left hover:text-purple-300 flex-1 text-[10px] text-slate-500"
              title="Clic para editar tipos de evento (boda,fiesta,empresa…)"
              data-testid={`dj-assistant-manual-track-hints-${track.id}`}
            >
              {track.event_type_hints?.length > 0
                ? track.event_type_hints.join(',')
                : <span className="italic">+ tipos evento</span>}
            </button>
          )}
        </div>
      </div>

      {savingField && <Loader2 className="w-3 h-3 animate-spin text-purple-300 flex-shrink-0" />}

      <button
        onClick={() => onDelete(track.id)}
        className="text-red-400 hover:text-red-300 px-1 flex-shrink-0"
        title="Borrar"
        data-testid={`dj-assistant-delete-manual-track-${track.id}`}
      >
        ✕
      </button>
    </div>
  );
};

const ManualTracksList = ({ tracks, setTracks, api, token, onDelete }) => {
  const [reordering, setReordering] = useState(false);
  const [collapsed, setCollapsed] = useState({}); // { groupId: bool }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Indexa tracks por grupo (preservando el orden de `tracks`, que viene priority asc)
  const groupedTracks = useMemo(() => {
    const out = {};
    for (const g of GROUPS) out[g.id] = [];
    for (const t of tracks) {
      const gid = roleToGroupId(t.role);
      if (out[gid]) out[gid].push(t);
    }
    return out;
  }, [tracks]);

  // Lookup: trackId → groupId
  const trackGroupMap = useMemo(() => {
    const m = {};
    for (const g of GROUPS) for (const t of groupedTracks[g.id]) m[t.id] = g.id;
    return m;
  }, [groupedTracks]);

  const persistReorder = async (newTracks) => {
    setReordering(true);
    try {
      await axios.post(
        `${api}/dj-assistant/manual-tracks/reorder`,
        { ids: newTracks.map(t => t.id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      toast.error(`Error reordenando: ${e?.response?.data?.detail || e.message}`);
      setTracks(tracks); // rollback
    } finally {
      setReordering(false);
    }
  };

  const persistRoleChange = async (trackId, newRole) => {
    try {
      const res = await axios.patch(
        `${api}/dj-assistant/manual-tracks/${trackId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data?.track;
    } catch (e) {
      toast.error(`Error cambiando rol: ${e?.response?.data?.detail || e.message}`);
      return null;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceGroup = trackGroupMap[active.id];
    // El `over` puede ser otro track O un group dropzone (id: `__group__warmup` etc.)
    let destGroup;
    if (typeof over.id === 'string' && over.id.startsWith('__group__')) {
      destGroup = over.id.slice('__group__'.length);
    } else {
      destGroup = trackGroupMap[over.id];
    }
    if (!sourceGroup || !destGroup) return;

    const activeTrack = tracks.find(t => t.id === active.id);
    if (!activeTrack) return;

    // Construir nuevo orden visual global (concatenando grupos por orden de GROUPS)
    let updatedTrack = activeTrack;

    if (sourceGroup !== destGroup) {
      // Cross-group: cambiar rol al defaultRole del destino
      const destDef = GROUPS.find(g => g.id === destGroup);
      const newRole = destDef?.defaultRole || destDef?.roles?.[0] || activeTrack.role;
      // Optimistic update local
      updatedTrack = { ...activeTrack, role: newRole };
      const newTracks = tracks.map(t => (t.id === activeTrack.id ? updatedTrack : t));
      // Reordenar dentro del nuevo grupo: insertar al final si over es un dropzone,
      // o sustituyendo posición del track-over
      const targetGroupTracks = newTracks
        .filter(t => roleToGroupId(t.role) === destGroup && t.id !== activeTrack.id);
      let insertAt = targetGroupTracks.length;
      if (!String(over.id).startsWith('__group__')) {
        const overIdx = targetGroupTracks.findIndex(t => t.id === over.id);
        if (overIdx >= 0) insertAt = overIdx;
      }
      targetGroupTracks.splice(insertAt, 0, updatedTrack);
      // Reconstruir orden global (cada grupo de GROUPS en orden visual)
      const rebuilt = [];
      for (const g of GROUPS) {
        if (g.id === destGroup) {
          rebuilt.push(...targetGroupTracks);
        } else {
          rebuilt.push(...newTracks.filter(t => roleToGroupId(t.role) === g.id && t.id !== activeTrack.id));
        }
      }
      // 1) Optimistic update UI
      setTracks(rebuilt);
      // 2) Persistir cambio de rol (espera para evitar race con reorder)
      const fresh = await persistRoleChange(activeTrack.id, newRole);
      if (fresh) {
        const rebuiltFresh = rebuilt.map(t => (t.id === fresh.id ? { ...t, ...fresh } : t));
        setTracks(rebuiltFresh);
        await persistReorder(rebuiltFresh);
      } else {
        // Rollback
        setTracks(tracks);
      }
    } else {
      // Misma agrupación: simple swap dentro del grupo
      const groupTrackIds = groupedTracks[sourceGroup].map(t => t.id);
      const oldIdx = groupTrackIds.indexOf(active.id);
      const newIdx = groupTrackIds.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return;
      const reorderedGroup = arrayMove(groupedTracks[sourceGroup], oldIdx, newIdx);
      // Reconstruir global
      const rebuilt = [];
      for (const g of GROUPS) {
        if (g.id === sourceGroup) rebuilt.push(...reorderedGroup);
        else rebuilt.push(...groupedTracks[g.id]);
      }
      setTracks(rebuilt);
      await persistReorder(rebuilt);
    }
  };

  const handleTrackUpdate = (updated) => {
    setTracks(prev => prev.map(t => (t.id === updated.id ? { ...t, ...updated } : t)));
  };

  const toggleGroup = (gid) => setCollapsed(prev => ({ ...prev, [gid]: !prev[gid] }));

  if (tracks.length === 0) {
    return (
      <p className="text-xs text-slate-500 text-center py-3">
        Aún no has añadido canciones con rol.
      </p>
    );
  }

  return (
    <div className="relative">
      {reordering && (
        <div className="absolute right-0 top-0 z-10 text-[10px] text-purple-300 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> guardando…
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="dj-assistant-manual-tracks-list">
          {GROUPS.map((g) => {
            const items = groupedTracks[g.id] || [];
            const isCollapsed = !!collapsed[g.id];
            return (
              <GroupBlock
                key={g.id}
                group={g}
                items={items}
                isCollapsed={isCollapsed}
                onToggle={() => toggleGroup(g.id)}
                api={api}
                token={token}
                onUpdate={handleTrackUpdate}
                onDelete={onDelete}
              />
            );
          })}
        </div>
      </DndContext>
    </div>
  );
};

// Componente del bloque-grupo. Tiene su propio SortableContext, header colapsable
// y dropzone interna (id especial `__group__<id>`) para soltar tracks de otro grupo
// cuando el grupo destino está vacío.
const GroupBlock = ({ group, items, isCollapsed, onToggle, api, token, onUpdate, onDelete }) => {
  const { setNodeRef: setDropRef, isOver } = useSortable({ id: `__group__${group.id}` });
  return (
    <div className={`rounded border ${isOver ? 'border-purple-400 bg-purple-500/5' : 'border-slate-700/50'}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1 text-xs font-semibold text-slate-200 hover:bg-slate-800/40 rounded-t"
        data-testid={`dj-assistant-manual-group-header-${group.id}`}
      >
        {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        <span className="text-base">{group.emoji}</span>
        <span className="flex-1 text-left">{group.label}</span>
        <span className="text-slate-500 font-normal">({items.length})</span>
      </button>
      {!isCollapsed && (
        <div ref={setDropRef} className="px-2 pb-2 pt-1 space-y-1">
          <SortableContext items={items.length > 0 ? items.map(t => t.id) : [`__group__${group.id}`]} strategy={verticalListSortingStrategy}>
            {items.length === 0 ? (
              <div className="text-[10px] text-slate-500 italic text-center py-2 border border-dashed border-slate-700/50 rounded">
                Suelta aquí para mover al grupo "{group.label}"
              </div>
            ) : (
              items.map((track, idx) => (
                <SortableTrack
                  key={track.id}
                  track={track}
                  api={api}
                  token={token}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  indexInGroup={idx}
                />
              ))
            )}
          </SortableContext>
        </div>
      )}
    </div>
  );
};

export default ManualTracksList;
